import { booksChanged } from '../lib/changed';
import { supabase } from '../lib/supabase';
import type { Suggestion } from './review';
import { addedDay } from './recent';

// Review's reads and saves (0042), as you: your waiting suggestions (from Claude's never-shared
// change record) and the review functions.

type Row = Record<string, unknown>;

function db() {
  if (!supabase) throw new Error('Not connected to a database.');
  return supabase;
}

/** Every suggestion waiting for you, with its batch. */
export async function loadSuggestions(): Promise<Suggestion[]> {
  // The database answers at most 1,000 rows at a time, so read in pages.
  const all: Row[] = [];
  for (let from = 0; from < 50000; from += 1000) {
    const { data, error } = await db()
      .from('claude_changes')
      .select('id, book_id, field, old_value, new_value, batch:claude_batches(id, name, kind, created_at)')
      .eq('mark', 'suggested')
      .is('undone_at', null)
      .not('book_id', 'is', null)
      .order('seq')
      .range(from, from + 999);
    if (error) throw new Error(error.message);
    const page = (data as Row[] | null) ?? [];
    all.push(...page);
    if (page.length < 1000) break;
  }
  return all.map((r) => {
    const b = (Array.isArray(r.batch) ? r.batch[0] : r.batch) as Row | null;
    return {
      id: r.id as string,
      bookId: r.book_id as string,
      field: (r.field as string) ?? '',
      value: r.new_value,
      before: r.old_value,
      batchId: (b?.id as string) ?? '',
      batchName: (b?.name as string) ?? 'A fix',
      batchKind: (b?.kind as string) ?? 'data_fix',
      batchOn: addedDay((b?.created_at as string) ?? ''),
    };
  });
}

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await db().rpc(fn, args);
  if (error) throw new Error(error.message);
  return data as T;
}

/** One change on the card, through the book's usual functions; returns its action (null: nothing changed). */
export async function callAction(fn: string, args: Record<string, unknown>): Promise<string | null> {
  const a = await rpc<string | null>(fn, args);
  booksChanged();
  return a;
}

export async function saveReview(book: string, actions: string[], accept: string[], replaced: string[]): Promise<{ group: string; marks: string[] }> {
  const r = await rpc<{ group: string; marks: string[] }>('review_save', { book, actions, accept, replaced });
  booksChanged();
  return r;
}

export async function undoReview(group: string, marks: string[]): Promise<void> {
  await rpc('review_undo', { grp: group, marks });
  booksChanged();
}

/** Accept all of one rule's suggestions (one or more batches); returns the marks changed. */
export async function acceptBatches(batches: string[]): Promise<string[]> {
  const out: string[] = [];
  for (const batch of batches) out.push(...((await rpc<string[] | null>('review_accept_batch', { batch })) ?? []));
  return out;
}

export const unaccept = (marks: string[]) => rpc<number>('review_unaccept', { marks });

/** One row's suggestions accepted (✓ Accept) or replaced (Change ▾), saved at once (0043). */
export async function markChanges(marks: string[], to: 'accepted' | 'replaced'): Promise<string[]> {
  return (await rpc<string[] | null>('review_mark_changes', { marks, to_mark: to })) ?? [];
}

/** Undo for a change made through the book's usual functions (Change ▾ on a row). */
export const undoAction = (action: string) => rpc('undo_action', { action }).then(() => booksChanged());
